import { createPortal } from 'react-dom';
import { useEffect, useReducer, useRef } from 'react';

import {
  PushNotificationsModel,
  IStateWithPermission,
  Events,
  Params,
} from '../../core/Model';

import { TIMEOUTS } from '../../constants/settings';

import { IAppDefaults } from '../../types';

import PushNotifications from '../../components/PushNotificationsTooltip';
import type { PushNotificationsTooltipProps } from '../../components/PushNotificationsTooltip/PushNotificationsTooltip.types';

import { reducer, initialState } from './reducer';
import {
  showAction,
  scrolledAction,
  initedAction,
  errorAction,
  timeoutInitAction,
} from './actions';

import { useScroll, UseScroll } from '../useScroll';

import { isLocalStorageAvailable } from '../../utils';

type UsePushNotificationsProps = Partial<Params> & {
  targetContainer: Element;
  componentsProps?: PushNotificationsTooltipProps;
  modelOptions: Partial<IAppDefaults>;
  modelEvents?: Partial<Events>;
  thresholdScreen?: UseScroll['thresholdScreen'];
  timeouts?: {
    initialization?: number;
  };
};

export const usePushNotifications = (props: UsePushNotificationsProps) => {
  const {
    isEnabled = true,
    useLocalStorageTimemark,
    timeMarkDelta,
    targetContainer,
    componentsProps,
    modelOptions,
    modelEvents,
    thresholdScreen,
    timeouts,
  } = props;

  const model = useRef<PushNotificationsModel | null>(null);
  const timeoutInitRef = useRef<number | undefined>(timeouts?.initialization);

  const [state, dispatch] = useReducer(reducer, initialState);

  const initialization = (
    inited: boolean,
    scrolled: boolean,
    timeoutInit: boolean
  ) => {
    if (inited && scrolled && timeoutInit) {
      dispatch(showAction(true));
    }
  };

  const handleModelReady = (stateModel: IStateWithPermission) => {
    if (!stateModel.isSubscribed) {
      // Если пользователь ранее разрешил пуши, и потом не отписывался от них
      // то не спрашиваем повторно
      if (
        stateModel?.permission &&
        stateModel.permission.browserPermission &&
        stateModel.permission.userPermission
      ) {
        model.current?.subscribe();
      } else {
        // Если подписка на пуши отсутствует, ставим флаг состояния об ответе с сервера
        dispatch(initedAction());
      }
    }
  };

  const handleScrolled = () => dispatch(scrolledAction());

  const handleCancel = () => {
    dispatch(showAction(false));
    model.current?.defer();
  };

  const handleSubscription = () => {
    model.current?.subscribe();
    dispatch(showAction(false));
  };

  /**
   * Проверяем доступен ли LocalStorage и выставляем флаг который передали.
   * @returns boolean
   */
  const checkLocalStorage = () =>
    isLocalStorageAvailable() && useLocalStorageTimemark;

  useEffect(() => {
    const timeoutsInit = setTimeout(() => {
      dispatch(timeoutInitAction());
    }, timeoutInitRef.current || TIMEOUTS.INITIALIZATION);

    return () => clearTimeout(timeoutsInit);
  }, []);

  useEffect(() => {
    initialization(state.inited, state.scrolled, state.timeoutInit);
  }, [state.inited, state.scrolled, state.timeoutInit]);

  useEffect(() => {
    model.current = new PushNotificationsModel(modelOptions, {
      isEnabled,
      useLocalStorageTimemark: checkLocalStorage(),
      timeMarkDelta,
    });

    model.current.init.events({
      ...modelEvents,
      error: (error) => {
        dispatch(errorAction());
        if (typeof modelEvents?.error === 'function') {
          modelEvents?.error(error);
        }
      },
      ready: (modelState) => {
        handleModelReady(modelState as IStateWithPermission);
        if (typeof modelEvents?.ready === 'function') {
          modelEvents?.ready(modelState);
        }
      },
    });

    return () => {
      model.current?.destroy();
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useScroll({
    isEnabled,
    scrolledCallback: handleScrolled,
    thresholdScreen,
  });

  const PushNotificationsWrapper = () => {
    return (
      <PushNotifications
        {...componentsProps}
        onSubscription={handleSubscription}
        onCancel={handleCancel}
      />
    );
  };

  return {
    createPushNotifications: () =>
      isEnabled && state.isShow && !state.isError
        ? createPortal(<PushNotificationsWrapper />, targetContainer)
        : null,
  };
};
